export interface RecorderResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

export type RecorderState = 'idle' | 'recording' | 'stopping';

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

function pickMime(): string {
  const MR = typeof MediaRecorder !== 'undefined' ? MediaRecorder : null;
  if (!MR) return 'audio/webm';
  for (const t of PREFERRED_MIME_TYPES) {
    if (MR.isTypeSupported(t)) return t;
  }
  return 'audio/webm';
}

/**
 * Simple MediaRecorder wrapper. Emits level updates via callback so the UI
 * can show a live amplitude meter. Does not own any React state.
 */
export class Recorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private levelTimer: number | null = null;
  private startedAt = 0;
  private mimeType = 'audio/webm';
  state: RecorderState = 'idle';

  onLevel?: (level: number) => void;
  onTick?: (durationMs: number) => void;

  async start(): Promise<void> {
    if (this.state !== 'idle') return;
    this.mimeType = pickMime();
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    this.recorder = new MediaRecorder(this.stream, { mimeType: this.mimeType });
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };

    this.audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const src = this.audioCtx.createMediaStreamSource(this.stream);
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 512;
    src.connect(this.analyser);

    this.startedAt = performance.now();
    this.levelTimer = window.setInterval(() => this.emitLevel(), 80);

    this.recorder.start(1000);
    this.state = 'recording';
  }

  private emitLevel(): void {
    if (!this.analyser) return;
    const buf = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / buf.length);
    this.onLevel?.(Math.min(1, rms * 3));
    this.onTick?.(performance.now() - this.startedAt);
  }

  async stop(): Promise<RecorderResult> {
    if (!this.recorder || this.state !== 'recording') {
      throw new Error('Recorder not running');
    }
    this.state = 'stopping';
    const mime = this.mimeType;
    const durationMs = performance.now() - this.startedAt;

    const blob = await new Promise<Blob>((resolve) => {
      const rec = this.recorder!;
      rec.onstop = () => resolve(new Blob(this.chunks, { type: mime }));
      rec.stop();
    });

    this.cleanup();
    this.state = 'idle';
    return { blob, mimeType: mime, durationMs };
  }

  cancel(): void {
    if (this.recorder && this.state === 'recording') {
      try {
        this.recorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.cleanup();
    this.state = 'idle';
  }

  private cleanup(): void {
    if (this.levelTimer) {
      clearInterval(this.levelTimer);
      this.levelTimer = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
    this.analyser = null;
    this.recorder = null;
  }
}

export function extForMime(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('mpeg')) return 'm4a';
  return 'webm';
}
